import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, throwError, of, map } from 'rxjs';
import { GlobalServicesService } from './global-services.service';
export type ProctoringPhotoFieldStatus = 'ok' | 'missing' | 'optional' | 'unknown';

@Injectable({
  providedIn: 'root'
})
export class FormBuilderService {

  fast_api_url = environment.HOST.FAST_API_URL;
  formSettings: BehaviorSubject<any> = new BehaviorSubject<any>({});
  private proctoringPhotoFieldCache = new Map<string, ProctoringPhotoFieldStatus>();
  private readonly PHOTO_FIELD_WARNINGS: Partial<Record<ProctoringPhotoFieldStatus, string>> = {
    missing: 'The selected registration form has no Photo field. Add a mandatory Photo field to the form to enable proctoring.',
    optional: 'The Photo field in the selected registration form must be marked as mandatory to enable proctoring.'
  };

  constructor(private http: HttpClient,private globalService:GlobalServicesService) { }

  proctoringPhotoFieldWarning(enabled: boolean, formId: string | null | undefined): Observable<string | null> {
    if (!enabled || !formId) {
      return of(null);
    }
    const cached = this.proctoringPhotoFieldCache.get(formId);
    if (cached) {
      return of(this.PHOTO_FIELD_WARNINGS[cached] ?? null);
    }
    return this.getFormFastApi(formId).pipe(
      map((response: any) => {
        const form = response?.body ?? response;
        const status = this.derivePhotoFieldStatus(form);
        if (status !== 'unknown') {
          this.proctoringPhotoFieldCache.set(formId, status);
        }
        return this.PHOTO_FIELD_WARNINGS[status] ?? null;
      }),
      catchError(() => of<string | null>(null))
    );
  }

  private derivePhotoFieldStatus(form: any): ProctoringPhotoFieldStatus {
    const fieldDetails = form?.field_details;
    if (!fieldDetails) {
      return 'unknown';
    }
    const allFields = Object.values(fieldDetails).flat() as any[];
    const photoField = allFields.find(
      (f) => f?.field_name === 'photo' && f?.field_type === 'File'
    );
    if (!photoField) {
      return 'missing';
    }
    return photoField.required === true ? 'ok' : 'optional';
  }

  private missingIdError(): Observable<never> {
    return throwError(() => new HttpErrorResponse({
      status: 400,
      statusText: 'Bad Request',
      error: { detail: 'This link is incomplete or has expired.' }
    }));
  }

  getEventFormDetailsFastApi(eventId: string, formId: string, useAdminToken: boolean = false): Observable<HttpResponse<any>> {
    if (!eventId || !formId) {
      return this.missingIdError();
    }
    const headers = useAdminToken ? undefined : new HttpHeaders({ 'use-candidate-token': 'true' });
    return this.http.get<HttpResponse<any>>(this.fast_api_url + `/events/${eventId}/forms/${formId}`, {
        observe: 'response',
        ...(headers ? { headers } : {})
      }).pipe(
        catchError((error: HttpErrorResponse) => throwError(() => error))
      );
    }
  
  validateCandidateFastApi(eventId: string, candidateId: string, formId: string | null, formType: string | null = null): Observable<HttpResponse<any>> {
    if (!eventId || !candidateId) {
      return this.missingIdError();
    }
    const url = formId 
    ? `${this.fast_api_url}/candidates/info/${candidateId}/events/${eventId}?form_id=${formId}&action_type=${formType}`
    : `${this.fast_api_url}/candidates/info/${candidateId}/events/${eventId}`;
    
    return this.http.get<HttpResponse<any>>( url, {
        observe: 'response',
        headers: new HttpHeaders({ 'use-candidate-token': 'true' })
      }).pipe(
        catchError((error: HttpErrorResponse) => throwError(() => error))
      );
    }
  
  submitRegistrationFormFastApi(event_payload: any, eventId: string): Observable<HttpResponse<any>> {
    return this.http.post<HttpResponse<any>>(this.fast_api_url + `/events/${eventId}/forms/registration`,event_payload, {
      observe: 'response',
      headers: new HttpHeaders({ 'use-candidate-token': 'true' })
    }).pipe(
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  submitDocumentationFormFastApi(event_payload: any, stageId: string): Observable<HttpResponse<any>> {
    return this.http.post<HttpResponse<any>>(this.fast_api_url + `/events/stages/${stageId}/forms/documentation`,event_payload, {
      observe: 'response',
      headers: new HttpHeaders({ 'use-candidate-token': 'true' })
    }).pipe(
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  submitOfferFormFastApi(event_payload: any, eventId: string, stageId: string): Observable<HttpResponse<any>> {
    return this.http.post<HttpResponse<any>>(this.fast_api_url + `/events/${eventId}/stages/${stageId}/forms/offer`,event_payload, {
      observe: 'response',
      headers: new HttpHeaders({ 'use-candidate-token': 'true' })
    }).pipe(
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  submitCandidateSkillScoreFastApi(eventUuid: string): Observable<HttpResponse<any>> {
    const genai_url = environment.HOST.HIRE_GENAI_URL;
    return this.http.post<HttpResponse<any>>(`${genai_url}/candidate/analyze?event_uuid=${eventUuid}`, {}, {
      observe: 'response',
      headers: new HttpHeaders({ 'use-candidate-token': 'true' })
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  getEventSpecificFormDetailsFastApi(eventId: string, lookupFlags?: {
    includeRoles?: boolean;
    includeInstitutes?: boolean;
    includeCertifications?: boolean;
    includeLanguages?: boolean;
    includeSkills?: boolean;
    includeSscBoards?: boolean;
    includeIntermediateBoards?: boolean;
    includeEntranceTests?: boolean;
    includeEducationDegrees?: boolean;
    includeEducationBranches?: boolean;
  }, paramsOrContext?: any, params?: any): Observable<HttpResponse<any>> {
    let url = this.fast_api_url + `/events/${eventId}/event-info`;    
    if (lookupFlags) {
      const fields: string[] = [];
      
      if (lookupFlags.includeRoles === true) fields.push('roles');
      if (lookupFlags.includeInstitutes === true) fields.push('institutes');
      if (lookupFlags.includeCertifications === true) fields.push('certifications');
      if (lookupFlags.includeLanguages === true) fields.push('languages');
      if (lookupFlags.includeSkills === true) fields.push('skills');
      if (lookupFlags.includeSscBoards === true) fields.push('ssc_boards');
      if (lookupFlags.includeIntermediateBoards === true) fields.push('intermediate_boards');
      if (lookupFlags.includeEntranceTests === true) fields.push('entrance_tests');
      if (lookupFlags.includeEducationDegrees === true) fields.push('education_degrees');
      if (lookupFlags.includeEducationBranches === true) fields.push('education_branches');
      
      if (fields.length > 0) {
        url += `?include=${fields.join(',')}`;
      }      
      const context = typeof paramsOrContext === 'string' ? paramsOrContext : undefined;
      if (context === 'form') {
        url += fields.length > 0 ? '&' : '?';
        url += 'origin=form';
      }
    }
    const finalParams = typeof paramsOrContext === 'object' ? paramsOrContext : params;

    return this.http.get<HttpResponse<any>>(url, {
        observe: 'response',
        params: finalParams || undefined,
        headers: new HttpHeaders({ 'use-candidate-token': 'true' })
      }).pipe(
        catchError((error: HttpErrorResponse) => throwError(() => error))
      );
    }

  submitFeedbackFormFastApi(form_payload: any, eventId: string, stageId: string, interviewId: string): Observable<HttpResponse<any>> {
    return this.http.post<HttpResponse<any>>(this.fast_api_url + `/events/${eventId}/stages/${stageId}/interviews/${interviewId}/forms/feedback`, form_payload, {
      observe: 'response'
    }).pipe(
      catchError(this.globalService.handleError.bind(this.globalService))
    );
  }

  UpdateFormFastApi(form_payload: any, eventId: string, formType: string, formEntryId: number): Observable<HttpResponse<any>> {
    return this.http.patch<HttpResponse<any>>(this.fast_api_url + `/events/${eventId}/forms/${formType}/${formEntryId}`, form_payload, {
      observe: 'response'
    }).pipe(
      catchError(this.globalService.handleError.bind(this.globalService))
    );
  }

  listFormsFastApi(form_type: string): Observable<HttpResponse<any>> {
    return this.http.get<HttpResponse<any>>(this.fast_api_url + `/forms/${form_type}/list`, {
      observe: 'response'
    }).pipe(
      catchError(this.globalService.handleError.bind(this.globalService))
    );
  }

  deleteFormFastApi(formId: string): Observable<HttpResponse<any>> {
    return this.http.delete<HttpResponse<any>>(this.fast_api_url + `/forms/${formId}`, {
      observe: 'response'
    }).pipe(
      catchError(this.globalService.handleError.bind(this.globalService))
    );
  }

  getFormFieldsFastApi(form_type: string): Observable<HttpResponse<any>> {
    return this.http.get<HttpResponse<any>>(this.fast_api_url + `/forms/${form_type}/fields`, {
      observe: 'response'
    }).pipe(
      catchError(this.globalService.handleError.bind(this.globalService))
    );
  }

  createNewFormFastApi(form_payload: any): Observable<HttpResponse<any>> {
    return this.http.post<HttpResponse<any>>(this.fast_api_url + `/forms`, form_payload, {
      observe: 'response'
    }).pipe(
      catchError(this.globalService.handleError.bind(this.globalService))
    );
  }
  updateFormFastApi(uuid: string, form_payload: any): Observable<HttpResponse<any>> {
    return this.http
      .put<HttpResponse<any>>(`${this.fast_api_url}/forms/${uuid}`, form_payload, {
        observe: 'response'
      })
      .pipe(catchError(this.globalService.handleError.bind(this.globalService)));
  }
  getFormFastApi(formId: string): Observable<HttpResponse<any>> {
    return this.http.get<HttpResponse<any>>(this.fast_api_url + `/forms/${formId}`, {
      observe: 'response'
    }).pipe(
      catchError(this.globalService.handleError.bind(this.globalService))
    );
  }

  mapFieldOptions(fieldName: string, list: any[], labelKey: any = [], valueKey: string | string[]): any[] {
      return list.map(item => ({
          label: fieldName === "institute" ? labelKey.map((key: string | number) => item[key]).filter((val: any) => val !== null && val !== undefined && val !== '').join(', ') : labelKey.map((key: string | number) => item[key]).join(', '),
          value: Array.isArray(valueKey) ? valueKey
              .map((key: string | number) => item[key])
              .filter((val: any) => val !== null && val !== undefined && val !== '')
              .join(', ') : item[valueKey]
      }));
  }

  getCustomFormFieldsFastApi(form_type: string): Observable<HttpResponse<any>> {
    return this.http.get<HttpResponse<any>>(this.fast_api_url + `/custom-fields/${form_type}/fields`, {
      observe: 'response'
    }).pipe(
      catchError(this.globalService.handleError.bind(this.globalService))
    );
  }

  createCustomFormFieldsFastApi(payload: any): Observable<HttpResponse<any>> {
    return this.http.post<HttpResponse<any>>(this.fast_api_url + `/custom-fields`, payload, {
      observe: 'response'
    }).pipe(
      catchError(this.globalService.handleError.bind(this.globalService))
    );
  }

  updateCustomFormFieldsFastApi(id: any, payload: any): Observable<HttpResponse<any>> {
    return this.http.put<HttpResponse<any>>(this.fast_api_url + `/custom-fields/${id}`, payload, {
      observe: 'response'
    }).pipe(
      catchError(this.globalService.handleError.bind(this.globalService))
    );
  }
}
